import { ZuiBaseElement } from '@zywave/zui-base';
import { findAssignedElements, findAssignedElement } from '@zywave/zui-base/dist/utils/find-assigned-element.js';
import { query, property } from 'lit/decorators.js';
import { html } from 'lit';
import { style } from './zui-breadcrumbs-css.js';
import { debounce } from '@zywave/zui-base/dist/utils/debounce.js';
import { screenBreakpoints } from '@zywave/zui-base/dist/utils/breakpoints.js';
import type { PropertyValues } from 'lit';

/**
 * `<zui-breadcrumbs>` is a wrapper around `<zui-breadcrumb>` elements.
 *
 * @element zui-breadcrumbs
 *
 * @slot - Default, unnamed slot; for inserting `<zui-breadcrumb>` elements into `<zui-breadcrumbs>`
 */
export class ZuiBreadcrumbs extends ZuiBaseElement {
  static get styles() {
    return [super.styles, style];
  }

  /**
   * Set to `true` if you do not want to truncate any of your breadcrumbs, making all visible
   */
  @property({ type: Boolean, attribute: 'show-all' })
  showAll = false;

  /**
   * Value determines how many visible breadcrumbs can be seen before truncation
   */
  @property({ type: Number, attribute: 'visible-count' })
  visibleCount = 4;

  @query('slot:not([name="first"])') private _slotEl: HTMLSlotElement | undefined;

  @query('slot[name="first"]') private _slotElFirstBreadcrumb: HTMLSlotElement | undefined;

  private _isMobile = false;

  get _slottedBreadcrumbs() {
    return findAssignedElements(this._slotEl, 'zui-breadcrumb');
  }

  get _slottedFirstBreadcrumb() {
    return findAssignedElement(this._slotElFirstBreadcrumb, 'zui-breadcrumb');
  }

  get _listOfBreadcrumbsToTruncate() {
    // create an array with the breadcrumbs to be hidden by truncation
    return this._slottedBreadcrumbs.slice(0, this._slottedBreadcrumbs.length - (this.visibleCount - 1));
  }

  get _listOfBreadcrumbsToReveal() {
    // array of breadcrumbs that will be visible if truncated
    return this._slottedBreadcrumbs.slice(
      this._slottedBreadcrumbs.length - (this.visibleCount - 1),
      this._slottedBreadcrumbs.length
    );
  }

  get _isTruncated() {
    // determine if breadcrumbs should truncate based on multiple properties
    if (this.showAll || this._isMobile || this.visibleCount >= this._slottedBreadcrumbs.length + 1) {
      return false;
    }
    return true;
  }

  private _onClickRevealHiddenBreadcrumbs() {
    // change truncated/hidden breadcrumbs to display: flex
    this._listOfBreadcrumbsToTruncate.forEach((crumb) => {
      crumb.removeAttribute('style');
    });
    // hide ...show-all-button
    this.showAll = true;
    this.requestUpdate();
  }

  private _moveFirstBreadcrumbToFirstSlot() {
    const breadcrumbs = this._slottedBreadcrumbs;
    const firstBreadcrumb = this._slottedBreadcrumbs[0];

    // remove slot attribute if someone is abusing slot names
    for (let i = 1; i < breadcrumbs.length; i++) {
      breadcrumbs[i].removeAttribute('slot');
    }

    // move first breadcrumb to first slot by changing slot attribute / value
    if (firstBreadcrumb) {
      firstBreadcrumb.setAttribute('slot', 'first');
    }
  }

  private _hideBreadcrumbs(arr: HTMLElement[]) {
    arr.forEach((crumb) => {
      crumb.style.display = 'none';
    });
  }

  private _unhideBreadcrumbs(arr: HTMLElement[]) {
    arr.forEach((crumb) => {
      crumb.removeAttribute('style');
    });
  }

  private _setupMediaQueryListener() {
    const breakpoint = (): string => {
      // convert breakpoint to be value - 1px for correct trigger point value
      const array = screenBreakpoints.xsmall.split('em');
      return `${parseInt(array[0]) - 1 / 16}em`;
    };

    const xsmallMq = window.matchMedia(`(max-width: ${breakpoint()})`);

    xsmallMq.addEventListener(
      'change',
      debounce((e: MediaQueryListEvent) => {
        this._isMobile = e.matches;
        if (!this._isTruncated) {
          this._unhideBreadcrumbs(this._listOfBreadcrumbsToTruncate);
        } else {
          this._hideBreadcrumbs(this._listOfBreadcrumbsToTruncate);
        }
        this.requestUpdate();
      }, 100)
    );

    this._isMobile = xsmallMq.matches;
  }

  protected firstUpdated(changedProps: PropertyValues) {
    super.firstUpdated(changedProps);
    this._setupMediaQueryListener();
    this._moveFirstBreadcrumbToFirstSlot();

    if (this._isTruncated) {
      this.requestUpdate();
      this._hideBreadcrumbs(this._listOfBreadcrumbsToTruncate);
    }

    // list for changes on <slot name="first"></slot>, shuffle and display breadcrumbs as needed
    // TODO: IE11 does not support `slotchange` event, possible solution is a mutationObserver, plan is to release and test to see if we even need these eventListeners / mutationObservers wtih dynamic nature of breadcrumbs
    this._slotElFirstBreadcrumb.addEventListener('slotchange', () => {
      if (!this._slottedFirstBreadcrumb) {
        this._moveFirstBreadcrumbToFirstSlot();
        this._slottedFirstBreadcrumb.removeAttribute('style');

        if (this._isTruncated) {
          this._unhideBreadcrumbs(this._listOfBreadcrumbsToReveal);
          this._hideBreadcrumbs(this._listOfBreadcrumbsToTruncate);
        }

        this.requestUpdate();
      }
    });

    // listen for changes on default <slot>, shuffle and display breadcrumbs as needed
    this._slotEl.addEventListener('slotchange', () => {
      if (this._isTruncated) {
        this._unhideBreadcrumbs(this._listOfBreadcrumbsToReveal);
        this._hideBreadcrumbs(this._listOfBreadcrumbsToTruncate);
      } else {
        this._unhideBreadcrumbs(this._slottedBreadcrumbs);
      }

      this.requestUpdate();
    });
  }

  render() {
    return html`
      <slot name="first"></slot>
      ${this._isTruncated
        ? html`
            <button class="show-all-button" @click="${this._onClickRevealHiddenBreadcrumbs}">
              <zui-icon icon="zui-left"></zui-icon>
              <span>...</span>
            </button>
          `
        : ''}
      <slot></slot>
    `;
  }
}

window.customElements.define('zui-breadcrumbs', ZuiBreadcrumbs);

declare global {
  interface HTMLElementTagNameMap {
    'zui-breadcrumbs': ZuiBreadcrumbs;
  }
}
